import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';

const baseStyles = ({ theme, as }) => css`
  font-family: ${theme.fontStack.default.name};
  font-weight: ${theme.fontStack.default.weight};
  display: block;
  margin-top: 0;
  margin-bottom: ${as === 'p' ? theme.spacings.bit : 0};
  letter-spacing: -0.18px;
`;

const sizeStyles = ({ theme, size, heading, lineHeight }) => {
  if (!size) {
    return null;
  }

  if (heading) {
    return css`
      font-size: ${theme.typography.headings[size].fontSize};
      line-height: ${lineHeight || theme.typography.headings[size].lineHeight};
    `;
  }

  return css`
    font-size: ${theme.typography.text[size].fontSize};
    line-height: ${lineHeight || theme.typography.text[size].lineHeight};
  `;
};

const boldStyles = ({ theme, bold }) =>
  bold &&
  css`
    font-family: ${theme.fontStack.bold.name};
    font-weight: ${theme.fontStack.bold.weight};
  `;

const lightStyles = ({ theme, light }) =>
  light &&
  css`
    font-family: ${theme.fontStack.light};
  `;

const italicStyles = ({ italic }) =>
  italic &&
  css`
    font-style: italic;
  `;

const designerStyles = ({ theme, designer }) =>
  designer &&
  css`
    font-family: ${theme.fontStack.designer};
  `;

const strikeThroughStyles = ({ theme, strike }) =>
  strike &&
  css`
    text-decoration: line-through;
    text-decoration-color: ${theme.colors.r700};
  `;

const marginStyles = ({ noMargin }) =>
  noMargin &&
  css`
    margin-bottom: 0;
  `;

const upperStyles = ({ upper }) =>
  upper &&
  css`
    text-transform: uppercase;
  `;

const centeredStyles = ({ centered }) =>
  centered &&
  css`
    text-align: center;
  `;

const StyledText = styled('p', {
  shouldForwardProp: (prop) => isPropValid(prop) && prop !== 'size',
})`
  ${baseStyles};
  ${marginStyles};
  ${boldStyles};
  ${lightStyles};
  ${italicStyles};
  ${designerStyles};
  ${strikeThroughStyles};
  ${upperStyles};
  ${sizeStyles};
  ${centeredStyles};
`;

const Text = ({ as, children, ...restProps }) => (
  <StyledText
    {...restProps}
    as={as}
    dangerouslySetInnerHTML={{
      __html: children,
    }}
  />
);

Text.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  size: PropTypes.oneOf(['atom', 'bit', 'byte', 'kilo', 'mega', 'giga', 'tera', 'peta', 'exa', 'zetta', 'yotta']),
  className: PropTypes.string,
  noMargin: PropTypes.bool,
  bold: PropTypes.bool,
  italic: PropTypes.bool,
  designer: PropTypes.bool,
  strike: PropTypes.bool,
  centered: PropTypes.bool,
  lineHeight: PropTypes.string,
  as: PropTypes.string,
};

Text.defaultProps = {
  as: 'p',
  size: 'kilo',
  className: '',
  children: null,
};

export default Text;
